import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export type UserRole = "owner" | "editor" | "viewer" | null;

export interface ActiveObjectState {
  activeProjectId: string | null;
  activeFolderId:  string | null;
  activeStyleId:   string | null;
  activeBomId:     string | null;
  activeThreadId:  string | null;
  activeUserRole:  UserRole;

  setActiveProjectId: (id: string | null) => void;
  setActiveFolderId:  (id: string | null) => void;
  setActiveStyleId:   (id: string | null) => void;
  setActiveBomId:     (id: string | null) => void;
  setActiveThreadId:  (id: string | null) => void;
  setActiveUserRole:  (role: UserRole) => void;

  // Compound actions wired to UI events:
  // folder click → activeFolderId + activeStyleId (clears downstream BOM/thread)
  openFolder:   (folderId: string, styleId: string | null) => void;
  // Tech Pack open → activeBomId + activeThreadId
  openTechPack: (bomId: string | null, threadId: string | null) => void;
}

export const useActiveObjectStore = create<ActiveObjectState>()(
  persist(
    (set) => ({
      activeProjectId: null,
      activeFolderId:  null,
      activeStyleId:   null,
      activeBomId:     null,
      activeThreadId:  null,
      activeUserRole:  null,

      setActiveProjectId: (id)   => set({ activeProjectId: id }),
      setActiveFolderId:  (id)   => set({ activeFolderId: id }),
      // Switching style clears BOM + thread so stale context never bleeds downstream.
      setActiveStyleId:   (id)   => set({ activeStyleId: id, activeBomId: null, activeThreadId: null }),
      setActiveBomId:     (id)   => set({ activeBomId: id }),
      setActiveThreadId:  (id)   => set({ activeThreadId: id }),
      setActiveUserRole:  (role) => set({ activeUserRole: role }),

      openFolder: (folderId, styleId) =>
        set({ activeFolderId: folderId, activeStyleId: styleId, activeBomId: null, activeThreadId: null }),

      openTechPack: (bomId, threadId) =>
        set({ activeBomId: bomId, activeThreadId: threadId }),
    }),
    {
      name: "lanfinitas-active-project",
      storage: createJSONStorage(() => localStorage),
      // Only persist activeProjectId — the rest is ephemeral per-session context.
      partialize: (state) => ({ activeProjectId: state.activeProjectId }),
    }
  )
);
